import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  FormControl,
  Link as ChakraLink,
  FormLabel,
  Stack,
  Input,
  Text,
  useToast,
  VStack,
  Divider,
  useDisclosure,
} from '@chakra-ui/react';
import axios from '../../../config/axios';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useGlobalStoreContext } from '../../../context';
import useFetch from '../../../hooks/useFetch';
import Loader from '../../../components/loader';
import Error from '../../../components/error';
import { compressImage } from '../../../utils';
import DangerZone from '../../../components/dangerZone';

const EditSponsor = () => {
  const { dispatch } = useGlobalStoreContext();
  const { handleSubmit, register } = useForm();
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [sponsorLoading, sponsorError, sponsor] = useFetch(`{
    sponsorById(id: "${id}") {
      name
      website
      image
    }
  }`);

  const {
    isOpen: dialogIsOpen,
    onOpen: dialogOpen,
    onClose: dialogOnClose,
  } = useDisclosure();

  useEffect(() => {
    dispatch({
      type: 'SETUP_PAGE',
      payload: {
        title: 'update sponsor',
        description: 'update event sponsor',
      },
    });
  }, [dispatch]);

  const cancelRef = useRef(null);

  const onSubmit = async (data: any) => {
    let formData = new FormData();
    for (const [key, value] of Object.entries<any>(data)) {
      if (typeof value === 'object') {
        formData.append(key, value[0]);
      } else {
        formData.append(key, value);
      }
    }
    try {
      setLoading(true);
      setLoading(true);
      await axios.put(`/event/sponsor/update/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({
        status: 'success',
        title: 'sponsor updated',
      });
      navigate('/sponsors');
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'failed to update sponsor',
        description: err.response ? err.response.data.message : '',
      });
    } finally {
      setLoading(false);
    }
  };

  const [deleteSponsorLoading, setDeleteSponsorLoading] = useState(false);

  const deleteSponsor = async () => {
    try {
      setDeleteSponsorLoading(true);
      await axios.delete(`/event/sponsor/delete/${id}`);
      navigate('/sponsors');
      toast({
        status: 'success',
        title: 'sponsor successfully deleted',
      });
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'problem deleting sponsor',
        description: err.response ? err.response.data.message : '',
      });
    } finally {
      setDeleteSponsorLoading(false);
    }
  };

  if (sponsorLoading) return <Loader />;

  if (sponsorError) return <Error />;

  return (
    <Box>
      <Breadcrumb color="GrayText" mb="4">
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/sponsors">
            Sponsors
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="">
            Update
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={dialogIsOpen}
        onClose={dialogOnClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bgColor="background.100">
            <AlertDialogHeader>Delete Sponsor</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this sponsor?
            </AlertDialogBody>
            <AlertDialogFooter gap="3">
              <Button ref={cancelRef} onClick={dialogOnClose}>
                Cancel
              </Button>
              <Button
                isLoading={deleteSponsorLoading}
                colorScheme="red"
                onClick={deleteSponsor}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack w="100%" alignItems="stretch" spacing="10">
          <Stack
            direction={{ base: 'column', md: 'row' }}
            alignItems="flex-start"
          >
            <FormControl id="name" isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                {...register('name')}
                variant="filled"
                borderRadius="lg"
                required
                defaultValue={sponsor.sponsorById.name}
              />
            </FormControl>
            <FormControl id="website" isRequired>
              <FormLabel>Website</FormLabel>
              <Input
                {...register('website')}
                variant="filled"
                borderRadius="lg"
                type="url"
                required
                defaultValue={sponsor.sponsorById.website}
              />
            </FormControl>
          </Stack>
          <FormControl id="website" isRequired>
            <FormLabel>Logo</FormLabel>
            <Text w="lg" isTruncated mb="2">
              Current
              <ChakraLink
                opacity="0.8"
                color="brand.100"
                fontSize="sm"
                isExternal
                href={compressImage(sponsor.sponsorById.image)}
              >
                {compressImage(sponsor.sponsorById.image)}
              </ChakraLink>
            </Text>
            <Input
              {...register('image')}
              variant="filled"
              type="file"
              accept="image/*"
              borderRadius="lg"
              required
            />
          </FormControl>
          <Button
            isLoading={loading}
            type="submit"
            variant="gradient"
            alignSelf="flex-end"
          >
            Update Sponsor
          </Button>
        </VStack>
      </form>
      <Divider my="10" />
      <DangerZone action={dialogOpen} text="Delete Sponsor" />
    </Box>
  );
};

export default EditSponsor;
